import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { api } from '../api/client';
import { fmtCHF, fmtCHF2 } from '../utils/format';
import { Contrat, ContratPrestation, Prestation, ScenarioBudget } from '../types';
import { BadgeTagRevenu, ChoixTagScenario } from '../components/TagScenarioRevenu';
import { HistoriqueRevenu } from '../components/HistoriqueRevenu';
import { PanierPrestationsModal } from '../components/PanierPrestationsModal';

const CATEGORIES_LABELS: Record<string, string> = {
  convention_mesures: 'Convention mesures',
  convention_cadre: 'Convention cadre',
};

/**
 * Référentiel des contrats — l'information qui fait foi :
 *   · convention mesures (DIRIS) : termes annuels prix × places, dont la
 *     grille Revenus dérive le revenu conventionné (mois déjà décomptés au
 *     mois-place, solde du plafond réparti sur les mois restants) ;
 *   · convention cadre (OAI) : tarifs par prestation, effectifs d'un mois en
 *     avant (renégociation en cours d'année), consommés par les flux OAI.
 *
 * Chaque ligne facturable (la mesure d'une convention mesures, chaque
 * prestation tarifée d'une convention cadre) déclare en plus son **panier de
 * prestations** : ce qu'elle recouvre réellement, à une date donnée. C'est ce
 * qui rend une mesure comparable d'une année sur l'autre — son contenu change,
 * les prestations non — et ce qui permettra de rapprocher le prix payé du coût
 * réel des prestations (charges + salaires via le module RH).
 */
export function ContratsPage() {
  const [contrats, setContrats] = useState<Contrat[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioBudget[]>([]);
  const [selection, setSelection] = useState<number | null>(null);
  const [prestations, setPrestations] = useState<Prestation[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [panier, setPanier] = useState<ContratPrestation | null>(null);

  const charger = useCallback(() => {
    setErreur(null);
    Promise.all([
      api.get<Contrat[]>('/contrats'),
      api.get<ScenarioBudget[]>('/revenus/scenarios'),
    ])
      .then(([cts, scs]) => {
        setContrats(cts);
        setScenarios(scs);
        setSelection((sel) => (sel != null && cts.some((c) => c.id === sel) ? sel : cts[0]?.id ?? null));
      })
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    api.get<Prestation[]>('/prestations').then(setPrestations).catch(() => {});
  }, []);

  const contrat = contrats.find((c) => c.id === selection) ?? null;
  const nomScenario = useCallback((id: number | null) => {
    if (id == null) return null;
    return scenarios.find((s) => s.id === id)?.nom ?? `#${id}`;
  }, [scenarios]);

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Contrats</h1>
          <span className="stat-pill">{contrats.length} contrats</span>
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <div className="toolbar">
        <div className="filter-chips">
          {contrats.map((c) => (
            <button key={c.id} className={`chip${selection === c.id ? ' active' : ''}`} onClick={() => setSelection(c.id)}>
              {c.libelle}
            </button>
          ))}
        </div>
      </div>

      {contrat && (
        <div className="card p-16">
          <div className="flex items-center gap-8 flex-wrap mb-8">
            <h2 className="text-base font-semi" style={{ margin: 0 }}>{contrat.libelle}</h2>
            <span className="badge badge-accent badge-sm">{CATEGORIES_LABELS[contrat.categorie] ?? contrat.categorie}</span>
            {contrat.programme && <span className="badge badge-sm">programme {contrat.programme}</span>}
            {!contrat.actif && <span className="badge badge-sm">inactif</span>}
            <span className="text-sm text-ink-2" style={{ marginLeft: 'auto' }}>
              Mandant : <strong>{contrat.client_acronyme ?? contrat.client_nom}</strong>
            </span>
          </div>
          {contrat.note && <p className="text-sm text-ink-2" style={{ marginTop: 0 }}>{contrat.note}</p>}

          {contrat.categorie === 'convention_mesures' && (
            <Termes contrat={contrat} scenarios={scenarios} nomScenario={nomScenario} onModifie={charger} onErreur={setErreur} />
          )}
          {contrat.categorie === 'convention_cadre' && (
            <Prestations contrat={contrat} scenarios={scenarios} nomScenario={nomScenario} onModifie={charger} onErreur={setErreur} />
          )}

          <Paniers contrat={contrat} onOuvrir={setPanier} />
        </div>
      )}

      {panier && contrat && (
        <PanierPrestationsModal
          ligne={panier}
          contratLibelle={contrat.libelle}
          prestations={prestations.filter((p) => p.actif)}
          onFermer={() => setPanier(null)}
          onModifie={() => { setPanier(null); charger(); }}
        />
      )}

      <p className="text-xs text-ink-3 mt-8">
        Les valeurs saisies ici font foi : la grille Revenus dérive le revenu conventionné DIRIS des termes annuels
        et applique les tarifs de prestation aux quantités OAI. Le décompte des places utilisées se saisit dans le
        module Mesures.
      </p>
    </div>
  );
}

// ── Paniers de prestations des lignes facturables ──────────────────────────
// Le panier en vigueur est résolu ici (dernier événement <= mois courant, par
// prestation) pour l'affichage ; la modale gère l'historique et les dates.
function Paniers({ contrat, onOuvrir }: {
  contrat: Contrat;
  onOuvrir: (ligne: ContratPrestation) => void;
}) {
  const maintenant = new Date();
  const mois = `${maintenant.getFullYear()}-${String(maintenant.getMonth() + 1).padStart(2, '0')}`;

  const panierDe = (ligne: ContratPrestation) => {
    const dernier = new Map<number, { libelle: string; inclus: boolean; depuis: string }>();
    for (const e of [...ligne.contenu].sort((a, b) => a.mois_effet.localeCompare(b.mois_effet))) {
      if (e.mois_effet.slice(0, 7) <= mois) {
        dernier.set(e.prestation_id, {
          libelle: e.prestation_libelle, inclus: e.inclus, depuis: e.mois_effet,
        });
      }
    }
    return [...dernier.values()].filter((e) => e.inclus);
  };

  return (
    <div className="mt-16">
      <h3 className="text-base font-semi mb-8">Paniers de prestations</h3>
      <table className="table table-compact">
        <thead>
          <tr>
            <th>Ligne facturable</th>
            <th>Ce qu'elle recouvre aujourd'hui</th>
            <th style={{ width: 120 }} />
          </tr>
        </thead>
        <tbody>
          {contrat.prestations.map((p) => {
            const panier = panierDe(p);
            return (
              <tr key={p.id}>
                <td className="font-semi">{p.libelle}</td>
                <td>
                  {panier.length === 0
                    ? <span className="text-ink-3 text-sm">panier non déclaré</span>
                    : panier.map((e) => (
                        <span key={e.libelle} className="badge badge-sm" style={{ marginRight: 4 }}
                          title={`Dans le panier depuis ${e.depuis.slice(0, 7)}`}>
                          {e.libelle}
                        </span>
                      ))}
                </td>
                <td>
                  <div className="flex justify-end">
                    <button className="btn btn-sm" onClick={() => onOuvrir(p)}>Composer</button>
                  </div>
                </td>
              </tr>
            );
          })}
          {contrat.prestations.length === 0 && (
            <tr><td colSpan={3} className="text-ink-3">Aucune ligne facturable pour ce contrat.</td></tr>
          )}
        </tbody>
      </table>
      <p className="text-xs text-ink-3">
        Le panier dit ce que recouvre une mesure à une date donnée : son contenu peut changer d'une année sur
        l'autre, alors que les prestations restent la même unité — c'est ce qui rend les comparaisons possibles
        et ce qui permettra de rapprocher le prix payé du coût réel.
      </p>
    </div>
  );
}

// ── Termes annuels d'une convention mesures ────────────────────────────────
// Saisie en prix par place et par MOIS (l'unité familière) ; la base stocke
// le prix annuel (prix mensuel × 12), tel qu'énoncé dans la convention.
function Termes({ contrat, scenarios, nomScenario, onModifie, onErreur }: {
  contrat: Contrat;
  scenarios: ScenarioBudget[];
  nomScenario: (id: number | null) => string | null;
  onModifie: () => void;
  onErreur: (e: string | null) => void;
}) {
  const [annee, setAnnee] = useState(String(new Date().getFullYear() + 1));
  const [prixMensuel, setPrixMensuel] = useState('');
  const [places, setPlaces] = useState('');
  const [tag, setTag] = useState<'definitif' | 'projete'>('definitif');
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [enCours, setEnCours] = useState(false);

  const prixAnnuel = (Number(prixMensuel) || 0) * 12;
  const plafond = prixAnnuel * (Number(places) || 0);

  const valide =
    Number(annee) >= 2000 &&
    prixMensuel.trim() !== '' && Number(prixMensuel) >= 0 &&
    places.trim() !== '' && Number(places) >= 0 &&
    commentaire.trim().length > 0 &&
    (tag === 'definitif' || scenarioId != null);

  function prefiller(t: Contrat['termes'][number]) {
    setAnnee(String(t.annee));
    setPrixMensuel(String(Math.round((Number(t.prix) / 12) * 100) / 100));
    setPlaces(String(Number(t.places)));
    setCommentaire('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    onErreur(null);
    try {
      await api.post(`/contrats/${contrat.id}/termes`, {
        annee: Number(annee), prix: Number(prixMensuel) * 12, places: Number(places),
        ...(tag === 'projete' ? { tag, scenarioId } : { tag }),
        commentaire: commentaire.trim(),
      });
      setPrixMensuel(''); setPlaces(''); setCommentaire('');
      onModifie();
    } catch (e) {
      onErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  async function supprimer(id: number) {
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    onErreur(null);
    try {
      await api.delete(`/contrats/termes/${id}`, { commentaire: c.trim() });
      onModifie();
    } catch (e) {
      onErreur((e as Error).message);
    }
  }

  return (
    <>
      <div className="formulaire-changement mb-16">
        <div className="formulaire-changement__titre">Nouveau terme annuel (négociation, correction, avenant ou hypothèse)</div>
        <div className="flex gap-12 flex-wrap items-end">
          <div className="form-group">
            <label className="form-label">Année</label>
            <input type="number" className="form-input" style={{ width: 90 }} min={2000} step={1}
              value={annee} onChange={(e) => setAnnee(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Prix / place / mois (CHF)</label>
            <input type="number" className="form-input" style={{ width: 140 }} min={0} step={0.05}
              value={prixMensuel} onChange={(e) => setPrixMensuel(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Places</label>
            <input type="number" className="form-input" style={{ width: 90 }} min={0} step={1}
              value={places} onChange={(e) => setPlaces(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Calculé</label>
            <div className="text-sm" style={{ paddingTop: 6 }}>
              CHF {fmtCHF(prixAnnuel)} /pl./an · plafond <strong>CHF {fmtCHF(plafond)}</strong> /an
              {' '}<span className="text-ink-3">({fmtCHF(plafond / 12)} /mois)</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Statut</label>
            <ChoixTagScenario tag={tag} scenarioId={scenarioId} scenarios={scenarios}
              onChange={(t, s) => { setTag(t); setScenarioId(s); }} />
          </div>
        </div>
        <div className="form-group mt-8">
          <label className="form-label">Commentaire (obligatoire)</label>
          <input className="form-input w-full" placeholder="Référence de la convention, correction, avenant, hypothèse de négociation…"
            value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        </div>
        <div className="flex justify-end mt-8">
          <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <h3 className="text-base font-semi mb-8">Termes</h3>
      <table className="table table-compact">
        <thead>
          <tr>
            <th>Année</th>
            <th className="text-right">Prix / pl. / mois</th>
            <th className="text-right">Prix / pl. / an</th>
            <th className="text-right">Places</th>
            <th className="text-right">Plafond / an</th>
            <th className="text-right"
              title="Plafond ÷ 12 — rythme moyen indicatif ; la grille Revenus facture au mois-place décompté puis répartit le solde du plafond">
              Rythme moyen (÷ 12)
            </th>
            <th>Statut</th>
            <th style={{ width: 64 }} />
          </tr>
        </thead>
        <tbody>
          {contrat.termes.map((t) => (
            <tr key={t.id} title={t.commentaire}>
              <td className="tabular-nums">{t.annee}</td>
              <td className="text-right tabular-nums">CHF {fmtCHF2(Number(t.prix) / 12)}</td>
              <td className="text-right tabular-nums">CHF {fmtCHF2(Number(t.prix))}</td>
              <td className="text-right tabular-nums">{Number(t.places)}</td>
              <td className="text-right tabular-nums">CHF {fmtCHF(Number(t.prix) * Number(t.places))}</td>
              <td className="text-right tabular-nums">CHF {fmtCHF((Number(t.prix) * Number(t.places)) / 12)}</td>
              <td><BadgeTagRevenu tag={t.tag} scenarioNom={nomScenario(t.scenario_id)} /></td>
              <td>
                <button className="btn btn-icon" title="Corriger (préremplit le formulaire — la nouvelle ligne remplacera celle-ci)"
                  onClick={() => prefiller(t)}>
                  <Pencil className="icon-sm" />
                </button>
                <button className="btn btn-icon" title="Supprimer le terme" onClick={() => supprimer(t.id)}>
                  <Trash2 className="icon-sm" />
                </button>
              </td>
            </tr>
          ))}
          {contrat.termes.length === 0 && <tr><td colSpan={8} className="text-ink-3">Aucun terme saisi.</td></tr>}
        </tbody>
      </table>
      <p className="text-xs text-ink-3">
        Un terme ne se modifie pas en place : pour corriger, utiliser ✎ (le formulaire se préremplit) puis
        enregistrer — pour une année et un scénario donnés, la projection du scénario l'emporte sur le terme
        définitif et, à statut égal, la dernière ligne créée fait foi. L'ancienne ligne peut ensuite être
        supprimée si elle résultait d'une erreur de saisie (et non d'un avenant).
      </p>
    </>
  );
}

// ── Prestations et tarifs d'une convention cadre ───────────────────────────
function Prestations({ contrat, scenarios, nomScenario, onModifie, onErreur }: {
  contrat: Contrat;
  scenarios: ScenarioBudget[];
  nomScenario: (id: number | null) => string | null;
  onModifie: () => void;
  onErreur: (e: string | null) => void;
}) {
  return (
    <div className="flex flex-col gap-16">
      {contrat.prestations.map((p) => (
        <PrestationBloc key={p.id} prestation={p} scenarios={scenarios} nomScenario={nomScenario}
          onModifie={onModifie} onErreur={onErreur} />
      ))}
      {contrat.prestations.length === 0 && <p className="text-ink-3 text-sm">Aucune prestation.</p>}
    </div>
  );
}

function PrestationBloc({ prestation, scenarios, nomScenario, onModifie, onErreur }: {
  prestation: ContratPrestation;
  scenarios: ScenarioBudget[];
  nomScenario: (id: number | null) => string | null;
  onModifie: () => void;
  onErreur: (e: string | null) => void;
}) {
  const maintenant = new Date();
  const [moisEffet, setMoisEffet] = useState(
    `${maintenant.getFullYear()}-${String(maintenant.getMonth() + 1).padStart(2, '0')}`
  );
  const [tarif, setTarif] = useState('');
  const [tag, setTag] = useState<'definitif' | 'projete'>('definitif');
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [ouvert, setOuvert] = useState(false);

  const tarifsTries = useMemo(
    () => prestation.tarifs.slice().sort((a, b) => a.mois_effet.localeCompare(b.mois_effet) || a.cree_le.localeCompare(b.cree_le)),
    [prestation.tarifs]
  );
  // Le tarif d'une prestation facturée au temps passé n'est pas un forfait
  // mensuel : l'unité doit apparaître partout où le montant se lit.
  const parUnite = prestation.unite === 'heure' ? ' / heure' : ' / mois';

  const valide =
    /^\d{4}-\d{2}$/.test(moisEffet) &&
    tarif.trim() !== '' && Number(tarif) >= 0 &&
    commentaire.trim().length > 0 &&
    (tag === 'definitif' || scenarioId != null);

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    onErreur(null);
    try {
      await api.post(`/contrats/prestations/${prestation.id}/tarif-evenements`, {
        moisEffet, tarif: Number(tarif),
        ...(tag === 'projete' ? { tag, scenarioId } : { tag }),
        commentaire: commentaire.trim(),
      });
      setTarif(''); setCommentaire('');
      onModifie();
    } catch (e) {
      onErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  async function supprimer(id: number) {
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    onErreur(null);
    try {
      await api.delete(`/contrats/tarif-evenements/${id}`, { commentaire: c.trim() });
      onModifie();
    } catch (e) {
      onErreur((e as Error).message);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-8 flex-wrap">
        <h3 className="text-base font-semi mb-8" style={{ marginBottom: 0 }}>{prestation.libelle}</h3>
        <span className="badge badge-sm" title={prestation.unite === 'heure'
          ? 'Facturé au temps passé (au quart d’heure entamé) — le nombre d’assurés ne dérive pas le montant'
          : 'Forfait mensuel par personne assurée'}>
          {prestation.unite === 'heure' ? 'à l’heure' : 'au mois'}
        </span>
        {prestation.positionTarifaire && (
          <span className="text-sm text-ink-3 tabular-nums" title="Position tarifaire du catalogue OAI — c’est elle qui rattache une facture à cette ligne">
            {prestation.positionTarifaire}
          </span>
        )}
      </div>
      <HistoriqueRevenu
        titre=""
        lignes={tarifsTries.map((t) => ({
          id: t.id, mois: t.mois_effet, valeur: `CHF ${fmtCHF2(Number(t.tarif))}${parUnite}`,
          tag: t.tag, scenarioNom: nomScenario(t.scenario_id), commentaire: t.commentaire,
        }))}
        onSupprimer={supprimer}
      />
      {!ouvert ? (
        <button className="btn btn-sm mt-8" onClick={() => setOuvert(true)}>Nouveau tarif dès un mois…</button>
      ) : (
        <div className="formulaire-changement mt-8">
          <div className="flex gap-12 flex-wrap items-end">
            <div className="form-group">
              <label className="form-label">À partir de</label>
              <input type="month" className="form-input" value={moisEffet} onChange={(e) => setMoisEffet(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Tarif (CHF{parUnite})</label>
              <input type="number" className="form-input" style={{ width: 120 }} min={0} step={0.05}
                value={tarif} onChange={(e) => setTarif(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Statut</label>
              <ChoixTagScenario tag={tag} scenarioId={scenarioId} scenarios={scenarios}
                onChange={(t, s) => { setTag(t); setScenarioId(s); }} />
            </div>
          </div>
          <div className="form-group mt-8">
            <label className="form-label">Commentaire (obligatoire)</label>
            <input className="form-input w-full" placeholder="Renégociation des conditions cadres, hypothèse…"
              value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
          <div className="flex justify-end gap-8 mt-8">
            <button className="btn btn-sm" onClick={() => setOuvert(false)}>Annuler</button>
            <button className="btn btn-accent btn-sm" disabled={!valide || enCours} onClick={enregistrer}>
              {enCours ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
